import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Crown, ShieldCheck, Sparkles, ChevronRight, Shield, Check, PartyPopper,
  Settings, Eye, UserCog, Camera, Megaphone, Inbox, BarChart3, MessageCircle,
  Lightbulb, Video, FileText, Phone, Image as ImageIcon, Award, Rocket, Circle, CheckCircle2, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { TierBadge, VerifiedBadge, FounderBadge, SilverBadge, GoldBadge } from "@/components/chef-badges";
import { tierFromChef } from "@/lib/chef-utils";

const PLAN_LABELS: Record<string, string> = {
  free: "Gratuito",
  silver_monthly: "Premium Silver",
  gold_monthly: "Premium Gold",
  premium_monthly: "Premium",
  premium_yearly: "Premium annuale",
};
import { useServerFn } from "@tanstack/react-start";
import { adminMe } from "@/lib/admin.functions";
import { getChefAnalytics } from "@/lib/analytics.functions";
import { createPortalSession } from "@/lib/payments.functions";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { derivePremiumStatus } from "@/lib/premium-status";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/dashboard/")({
  head: () => ({ meta: [{ title: "Dashboard — ChefConnect" }] }),
  component: DashboardPage,
});

function DashboardPage() {
  const { user, isChef, loading: authLoading } = useAuth();
  const adminFn = useServerFn(adminMe);
  const adminQ = useQuery({ enabled: !!user, queryKey: ["admin-me"], queryFn: () => adminFn() });
  // Sprint 25.2 — gli admin entrano direttamente nel pannello amministrativo.
  const navigate = useNavigate();
  const isAdmin = !!adminQ.data?.isAdmin;
  useEffect(() => {
    if (isAdmin) navigate({ to: "/dashboard/admin", replace: true });
  }, [isAdmin, navigate]);
  const analyticsFn = useServerFn(getChefAnalytics);
  const analyticsQ = useQuery({
    enabled: !!user,
    queryKey: ["chef-analytics", user?.id],
    queryFn: () => analyticsFn({ data: { days: 30 } }),
  });

  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("chefs").select("*").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  // Sprint 23.7A — login/dashboard NON è più considerato attività.
  // Solo modifiche concrete (profilo, foto, menù, annunci, risposte) aggiornano last_active_at.

  const subQ = useQuery({
    enabled: !!user,
    queryKey: ["my-sub", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("subscriptions").select("*").eq("chef_id", user!.id)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();
      return data;
    },
  });

  const paymentsQ = useQuery({
    enabled: !!user,
    queryKey: ["my-payments", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("payments").select("*").eq("chef_id", user!.id).order("created_at", { ascending: false }).limit(10);
      return data ?? [];
    },
  });

  // Onboarding: conteggi media/annunci e stato verifica.
  const onboardingQ = useQuery({
    enabled: !!user,
    queryKey: ["my-onboarding", user?.id],
    queryFn: async () => {
      const [media, listings, verif, contact] = await Promise.all([
        supabase.from("chef_media").select("id, kind, is_cover").eq("chef_id", user!.id),
        supabase.from("chef_listings").select("id", { count: "exact", head: true }).eq("chef_id", user!.id),
        supabase.from("verification_requests").select("status").eq("chef_id", user!.id).order("submitted_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("chef_contacts").select("whatsapp").eq("chef_id", user!.id).maybeSingle(),
      ]);
      const mediaRows = media.data ?? [];
      return {
        photos: mediaRows.filter((m) => m.kind === "photo").length,
        hasCover: mediaRows.some((m) => m.is_cover),
        hasMenu: mediaRows.some((m) => m.kind === "menu_pdf"),
        hasVideo: mediaRows.some((m) => m.kind === "video"),
        listings: listings.count ?? 0,
        verificationStatus: (verif.data?.status ?? null) as string | null,
        hasWhatsApp: !!(contact.data?.whatsapp && contact.data.whatsapp.trim().length > 0),
      };
    },
  });

  // Contact requests count — per il riepilogo.
  const requestsQ = useQuery({
    enabled: !!user,
    queryKey: ["my-requests-count", user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("contact_requests").select("id", { count: "exact", head: true })
        .eq("chef_id", user!.id);
      return count ?? 0;
    },
  });

  const chef = chefQ.data;
  const sub = subQ.data;
  // SSoT (Sprint 3.3): unica derivazione Premium per la Dashboard.
  const premiumStatus = derivePremiumStatus({ premium_until: chef?.premium_until ?? null });
  const isPremium = premiumStatus.isPremium;
  const remaining = premiumStatus.daysRemaining;
  // Sprint 19.1 — tier corrente per badge / etichette
  const tier = tierFromChef({
    premium_tier: (chef as { premium_tier?: string | null } | null | undefined)?.premium_tier ?? null,
    premium_until: chef?.premium_until ?? null,
  });

  // Profile completeness — basato su campi reali del profilo.
  const ob = onboardingQ.data;
  const verified = chef?.verified_badge === true;
  const completeness = [
    { key: "profile", label: "Crea il profilo Chef", cta: "Inizia", icon: UserCog, done: !!chef, to: "/dashboard/profilo", benefit: "Senza un profilo i clienti non possono trovarti." },
    { key: "bio", label: "Scrivi una biografia", cta: "Scrivi la bio", icon: FileText, done: !!(chef?.bio && chef.bio.trim().length > 20), to: "/dashboard/profilo", benefit: "Raccontare la tua storia aiuta i clienti a sceglierti." },
    { key: "city", label: "Aggiungi città e regione", cta: "Imposta zona", icon: Megaphone, done: !!(chef?.city && chef?.region), to: "/dashboard/profilo", benefit: "Compari nelle ricerche per zona geografica." },
    { key: "cuisines", label: "Indica le tue cucine", cta: "Seleziona cucine", icon: Sparkles, done: (chef?.cuisines?.length ?? 0) > 0, to: "/dashboard/profilo", benefit: "I clienti filtrano per tipo di cucina." },
    { key: "specialties", label: "Aggiungi le specialità", cta: "Aggiungi specialità", icon: Sparkles, done: (chef?.specialties?.length ?? 0) > 0, to: "/dashboard/profilo", benefit: "Le specialità appaiono come badge nella tua card." },
    { key: "rate", label: "Imposta la tariffa oraria", cta: "Imposta tariffa", icon: Award, done: !!chef?.hourly_rate, to: "/dashboard/profilo", benefit: "Una tariffa chiara genera richieste più qualificate." },
    { key: "cover", label: "Scegli la foto di copertina", cta: "Scegli cover", icon: ImageIcon, done: !!ob?.hasCover, to: "/dashboard/foto", benefit: "La copertina è la prima immagine che i clienti vedono." },
    { key: "photos", label: "Carica almeno 3 foto", cta: "Carica foto", icon: Camera, done: (ob?.photos ?? 0) >= 3, to: "/dashboard/foto", benefit: "I profili con più foto aiutano i clienti a capire il tuo stile." },
    { key: "video", label: "Carica un video di presentazione", cta: "Carica video", icon: Video, done: !!ob?.hasVideo, to: "/dashboard/foto", benefit: "Un breve video ti rende più riconoscibile." },
    { key: "menu", label: "Carica un menù in PDF", cta: "Carica menù", icon: FileText, done: !!ob?.hasMenu, to: "/dashboard/foto", benefit: "I clienti possono sfogliare la tua proposta prima di contattarti." },
    { key: "whatsapp", label: "Configura WhatsApp", cta: "Aggiungi WhatsApp", icon: Phone, done: !!ob?.hasWhatsApp, to: "/dashboard/profilo", benefit: "Permetti ai clienti di contattarti più rapidamente." },
    { key: "verified", label: "Invia documenti per la verifica", cta: "Avvia verifica", icon: ShieldCheck, done: verified, to: "/dashboard/verifica", benefit: "Il badge Verificato aumenta la fiducia dei clienti." },
    { key: "listing", label: "Pubblica il primo annuncio", cta: "Pubblica annuncio", icon: Megaphone, done: (ob?.listings ?? 0) > 0, to: "/dashboard/annunci", benefit: "Gli annunci descrivono i tuoi servizi e generano richieste." },
  ];
  const completed = completeness.filter((c) => c.done).length;
  const progress = Math.round((completed / completeness.length) * 100);
  const missing = completeness.filter((c) => !c.done);
  const nextStep = missing[0];

  // Frase dinamica sotto il saluto — onesta, basata su stato reale.
  const heroSubtitle = !chef
    ? "Crea il tuo profilo Chef per cominciare a ricevere richieste."
    : !verified
      ? "Completa la verifica per sbloccare le richieste dei clienti."
      : progress < 100
        ? `Il tuo profilo è online. Completalo per ricevere più richieste (${progress}%).`
        : (requestsQ.data ?? 0) === 0
          ? "Il tuo profilo è online e pronto per ricevere nuove richieste."
          : `Hai ${requestsQ.data} richieste ricevute. Continua così!`;

  // Suggerimenti intelligenti — appaiono solo quando applicabili, mai inventati.
  const tips: Array<{ key: string; icon: typeof Camera; title: string; body: string; to: string; cta: string }> = [];
  if (chef && (ob?.photos ?? 0) > 0 && (ob?.photos ?? 0) < 3) {
    tips.push({ key: "few-photos", icon: Camera, title: `Hai solo ${ob?.photos} foto.`, body: "Aggiungine altre per mostrare meglio il tuo lavoro.", to: "/dashboard/foto", cta: "Carica foto" });
  }
  if (chef && !ob?.hasVideo) {
    tips.push({ key: "no-video", icon: Video, title: "Non hai ancora caricato un video.", body: "Presentarti in pochi secondi aiuta i clienti a conoscerti meglio.", to: "/dashboard/foto", cta: "Aggiungi video" });
  }
  if (chef && !ob?.hasWhatsApp) {
    tips.push({ key: "no-wa", icon: MessageCircle, title: "Attiva WhatsApp.", body: "Permetti ai clienti di contattarti più rapidamente.", to: "/dashboard/profilo", cta: "Configura WhatsApp" });
  }
  if (chef && tier === "free") {
    tips.push({ key: "upgrade", icon: Crown, title: "Passa a Premium Silver o Gold.", body: "Aumenta la visibilità del tuo profilo nei risultati di ricerca.", to: "/premium", cta: "Scopri Premium" });
  }
  if (chef && !verified && ob?.verificationStatus !== "pending") {
    tips.push({ key: "verify", icon: ShieldCheck, title: "Verifica i tuoi documenti.", body: "Il badge Verificato aumenta drasticamente la fiducia dei clienti.", to: "/dashboard/verifica", cta: "Avvia la verifica" });
  }

  // Timeline "Il tuo percorso" — solo eventi realmente accaduti.
  const timeline = [
    { key: "register", label: "Registrazione su ChefConnect", done: true },
    { key: "profile", label: "Profilo Chef completato", done: !!chef && progress >= 80 },
    { key: "verify", label: "Verifica approvata", done: verified },
    { key: "listing", label: "Primo annuncio pubblicato", done: (ob?.listings ?? 0) > 0 },
    { key: "premium", label: tier === "gold" ? "Premium Gold attivo" : tier === "silver" ? "Premium Silver attivo" : "Premium attivato", done: tier !== "free" },
    { key: "request", label: "Prima richiesta ricevuta", done: (requestsQ.data ?? 0) > 0 },
  ];

  // Saluto dinamico.
  const firstName = (chef?.display_name || user?.email?.split("@")[0] || "Chef").split(" ")[0];
  const hour = new Date().getHours();
  const greeting = hour < 6 ? "Buona notte" : hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  const portalFn = useServerFn(createPortalSession);

  const openPortal = async () => {
    if (!isPaymentsConfigured()) { toast.error("Pagamenti non ancora attivi. Riprova più tardi."); return; }
    try {
      const res = await portalFn({ data: { environment: getStripeEnvironment(), returnUrl: window.location.origin + "/dashboard" } });
      if ("error" in res) throw new Error(res.error);
      window.open(res.url, "_blank");
    } catch (e: any) { toast.error(e.message || "Errore"); }
  };

  // Sprint 23.6 — Verifica come primo passo obbligatorio.
  // Finché lo chef non è verificato, la dashboard mostra SOLO lo stato verifica
  // e nasconde profilo / foto / video / menù / annunci. Le route relative sono
  // già bloccate via beforeLoad (requireVerifiedChef).
  // Eccezione: admin vede tutto.
  const isVerificationGate =
    !adminQ.data?.isAdmin &&
    !chefQ.isLoading &&
    (isChef || !!chef) &&
    !verified;

  // Sprint 24.5 — Cliente normale: nessun onboarding chef.
  // Mostriamo una dashboard cliente semplice (saluto + azioni utili).
  const isPlainClient =
    !authLoading &&
    !chefQ.isLoading &&
    !adminQ.data?.isAdmin &&
    !isChef &&
    !chef;
  if (isPlainClient) {
    const greetingName = (user?.email?.split("@")[0] || "").split(" ")[0];
    return (
      <div className="container mx-auto max-w-3xl px-4 py-10">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
        <h1 className="mt-1 font-display text-4xl">
          {greeting}{greetingName ? `, ${greetingName}` : ""} 👋
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Benvenuto su ChefConnect. Cerca uno chef nella tua città o, se cucini tu, diventa Chef e inizia a ricevere richieste.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <Link to="/search" className="group rounded-xl border border-border bg-card p-6 transition hover:border-gold">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gold-muted/40 p-2 text-gold"><Sparkles className="h-5 w-5" /></div>
              <div>
                <p className="font-medium">Cerca uno chef</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Filtra per città, cucina e budget.</p>
              </div>
            </div>
          </Link>
          <Link to="/diventa-chef" className="group rounded-xl border border-gold/50 bg-gold-muted/20 p-6 transition hover:border-gold">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gold p-2 text-gold-foreground"><Crown className="h-5 w-5" /></div>
              <div>
                <p className="font-medium">Diventa Chef</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Crea il tuo profilo e ricevi richieste.</p>
              </div>
            </div>
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="ghost"><Link to="/dashboard/account">Impostazioni account</Link></Button>
        </div>
      </div>
    );
  }

  if (isVerificationGate) {
    const status = ob?.verificationStatus;
    const greetingName = (chef?.display_name || user?.email?.split("@")[0] || "").split(" ")[0];
    return (
      <div className="container mx-auto max-w-3xl px-4 py-10">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
        <h1 className="mt-1 font-display text-4xl">Benvenuto{greetingName ? `, ${greetingName}` : ""} 👋</h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Per diventare uno Chef su ChefConnect il primo passo è la verifica dell'identità e delle qualifiche. Solo dopo l'approvazione potrai creare il profilo pubblico, caricare foto, video, menù e pubblicare annunci.
        </p>

        {/* Roadmap chiara dei 4 passi */}
        <ol className="mt-8 space-y-3">
          {[
            { n: 1, label: "Verifica identità", done: status === "approved", current: status !== "approved" },
            { n: 2, label: "Completa il profilo", done: false, current: false },
            { n: 3, label: "Pubblica il primo annuncio", done: false, current: false },
            { n: 4, label: "Ricevi richieste dai clienti", done: false, current: false },
          ].map((s) => (
            <li
              key={s.n}
              className={`flex items-center gap-3 rounded-xl border p-4 ${
                s.current ? "border-gold bg-gold-muted/20" : "border-border bg-card"
              }`}
            >
              {s.done ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-gold" />
              ) : (
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-medium ${
                  s.current ? "bg-gold text-gold-foreground" : "bg-muted text-muted-foreground"
                }`}>{s.n}</span>
              )}
              <span className={`text-sm ${s.current ? "font-medium" : s.done ? "" : "text-muted-foreground"}`}>{s.label}</span>
            </li>
          ))}
        </ol>

        {/* Card di stato + CTA */}
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 shrink-0 text-blue-600" />
            <div className="flex-1">
              {status === "pending" ? (
                <>
                  <h2 className="font-display text-2xl">🟡 Verifica in revisione</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Abbiamo ricevuto i tuoi documenti. Il nostro team li sta esaminando: di norma rispondiamo entro 24-48 ore. Riceverai una notifica appena la verifica sarà completata.
                  </p>
                  <Button asChild variant="outline" className="mt-4">
                    <Link to="/dashboard/verifica">Vedi stato verifica</Link>
                  </Button>
                </>
              ) : status === "rejected" ? (
                <>
                  <h2 className="font-display text-2xl">❌ Verifica rifiutata</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    La tua richiesta non è stata approvata. Puoi reinviare nuovi documenti dalla pagina di verifica.
                  </p>
                  <Button asChild className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90">
                    <Link to="/dashboard/verifica">Reinvia documenti</Link>
                  </Button>
                </>
              ) : (
                <>
                  <h2 className="font-display text-2xl">Inizia la tua verifica</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Carica i tuoi documenti (documento d'identità ed eventuali qualifiche professionali) per iniziare. Il processo richiede pochi minuti: i documenti sono privati e visibili solo allo staff.
                  </p>
                  <Button asChild className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90">
                    <Link to="/dashboard/verifica">Avvia la verifica</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Cosa succede dopo l'approvazione */}
        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-xl">Cosa si sblocca dopo l'approvazione</h3>
          <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Creazione del profilo pubblico</li>
            <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Upload di foto, video e menù PDF</li>
            <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Pubblicazione degli annunci</li>
            <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Ricezione richieste dai clienti</li>
          </ul>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button asChild variant="ghost"><Link to="/dashboard/account">Impostazioni account</Link></Button>
          {adminQ.data?.isAdmin && (
            <Button asChild variant="outline"><Link to="/dashboard/admin">Pannello Admin</Link></Button>
          )}
        </div>
      </div>
    );
  }

  const requestsCount = requestsQ.data ?? 0;
  const summary = [
    { label: "Richieste ricevute", value: requestsCount, icon: Inbox, to: "/dashboard/richieste" },
    { label: "Annunci pubblicati", value: ob?.listings ?? 0, icon: Megaphone, to: "/dashboard/annunci" },
    { label: "Foto caricate", value: ob?.photos ?? 0, icon: Camera, to: "/dashboard/foto" },
    {
      label: "Premium",
      value: isPremium ? `${remaining}g` : "—",
      sub: isPremium ? "attivo" : "non attivo",
      icon: Crown,
      to: "/premium",
    },
    {
      label: "Verifica",
      value: verified ? "OK" : ob?.verificationStatus === "pending" ? "In revisione" : "Da fare",
      icon: ShieldCheck,
      to: "/dashboard/verifica",
    },
  ];

  const actions = [
    { label: "Completa il profilo", desc: "Aggiorna bio, città e tariffe", icon: UserCog, to: "/dashboard/profilo" },
    { label: "Gestisci foto e menù", desc: "Carica foto, video e menù PDF", icon: Camera, to: "/dashboard/foto" },
    { label: "Pubblica un annuncio", desc: "Servizi, eventi e proposte", icon: Megaphone, to: "/dashboard/annunci" },
    { label: "Verifica documenti", desc: "Ottieni il badge Verificato", icon: ShieldCheck, to: "/dashboard/verifica" },
    { label: "Gestisci Premium", desc: "Piani, scadenze e fatturazione", icon: Crown, to: "/premium" },
    { label: "Impostazioni account", desc: "Email, password e privacy", icon: Settings, to: "/dashboard/account" },
  ];

  return (
    <div className="container mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Dashboard</p>
          <h1 className="mt-1 font-display text-4xl">{greeting}, {firstName} 👋</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{heroSubtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {chef?.slug && (
            <Button asChild variant="outline">
              <Link to="/chef/$slug" params={{ slug: chef.slug }}>
                <Eye className="mr-1.5 h-4 w-4" /> Visualizza profilo pubblico
              </Link>
            </Button>
          )}
          {!chef && (
            <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to="/dashboard/profilo">Crea il tuo profilo Chef</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Sprint 23.7A — Bentornato: profilo nascosto, riattivazione esplicita */}
      {chef && (chef as any).hidden_for_inactivity === true && (
        <ReactivationCard
          chefId={user!.id}
          verified={verified}
          hasActiveListing={(ob?.listings ?? 0) > 0}
          onReactivated={() => { void chefQ.refetch(); }}
        />
      )}
      {chef && (chef as any).hidden_for_inactivity !== true && (chef as any).reactivation_pending === true && (
        <div className="mt-6 rounded-2xl border border-green-500/40 bg-green-500/5 p-5">
          <h3 className="font-display text-xl">✅ Profilo riattivato</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Il tuo profilo è di nuovo online. Controlla che le informazioni siano aggiornate.
          </p>
        </div>
      )}

      {/* Sprint 20.5 — Next-Step Card: per chef incompleti, mostra il prossimo singolo passo in evidenza. */}
      {chef && progress < 100 && nextStep && (
        <div className="mt-6 rounded-2xl border border-gold/50 bg-gradient-to-br from-gold-muted/30 to-gold-muted/5 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold text-gold-foreground">
                <nextStep.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gold">Prossimo passo · {completed + 1} di {completeness.length}</p>
                <h2 className="mt-0.5 font-display text-2xl">{nextStep.label}</h2>
                <p className="mt-1 max-w-xl text-sm text-muted-foreground">{nextStep.benefit}</p>
              </div>
            </div>
            <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to={nextStep.to}>{nextStep.cta} <ChevronRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
          <Progress value={progress} className="mt-5 h-1.5" />
        </div>
      )}

      {/* Riepilogo rapido — capire tutto in 5 secondi. */}
      <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {summary.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            className="group rounded-xl border border-border bg-card p-4 transition hover:border-gold"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className="h-4 w-4 text-muted-foreground transition group-hover:text-gold" />
            </div>
            <div className="mt-2 font-display text-2xl">{s.value}</div>
            {"sub" in s && s.sub && <p className="text-xs text-muted-foreground">{s.sub}</p>}
          </Link>
        ))}
      </div>

      {!chef && (
        <div className="mt-8 rounded-xl border border-gold/40 bg-gold-muted/20 p-6">
          <h2 className="font-display text-2xl">🎁 Sei a un passo dal bonus di lancio</h2>
          <p className="mt-2 text-muted-foreground">
            Crea ora il tuo profilo Chef e ricevi automaticamente <strong>30 giorni di Premium gratis</strong>, badge Founder permanente e priorità nelle ricerche.
          </p>
          <Button asChild className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90">
            <Link to="/dashboard/profilo">Completa il profilo ora <ChevronRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      )}

      {/* Founder Pack — visibile solo se l'utente è effettivamente Founder. */}
      {chef?.founder_badge && (
        <div className="mt-8 rounded-xl border border-gold/60 bg-gradient-to-br from-gold-muted/40 to-gold-muted/10 p-6">
          <div className="flex items-start gap-3">
            <PartyPopper className="h-6 w-6 shrink-0 text-gold" />
            <div className="flex-1">
              <h2 className="font-display text-2xl">Congratulazioni! Sei un Chef Founder</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Sei tra i primi 100 chef della piattaforma. I tuoi vantaggi:
              </p>
              <ul className="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
                <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Badge Founder permanente</li>
                {isPremium && (
                  <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Premium gratuito per {premiumStatus.daysRemaining} giorni</li>
                )}
                <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Visibilità esclusiva nei risultati</li>
                <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Priorità nelle ricerche</li>
              </ul>
              {isPremium && premiumStatus.premiumUntil && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Premium valido fino al <strong>{premiumStatus.premiumUntil.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stato del tuo profilo — completeness + checklist cliccabile. */}
      {chef && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl">
                {progress === 100 ? "Ottimo lavoro! Il tuo profilo è completo." : "Stato del tuo profilo"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {progress === 100
                  ? "Hai completato tutti i passi base. Ora puoi concentrarti sulla crescita."
                  : "Più completo è il profilo, più richieste riceverai."}
              </p>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl text-gold">{progress}%</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{completed} di {completeness.length}</div>
            </div>
          </div>
          <Progress value={progress} className="mt-4 h-2" />
          {progress < 100 ? (
            <div className="mt-6">
              <p className="text-sm font-medium">Completa il tuo profilo</p>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {missing.map((c) => (
                  <li key={c.key}>
                    <Link
                      to={c.to}
                      className="group flex items-center gap-3 rounded-lg border border-border bg-background/40 px-3 py-2.5 transition hover:border-gold/60"
                    >
                      <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 text-sm text-muted-foreground">{c.label}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-gold" />
                    </Link>
                  </li>
                ))}
              </ul>
              {completed > 0 && (
                <p className="mt-3 text-xs text-muted-foreground">
                  {completed} {completed === 1 ? "passo completato" : "passi completati"} su {completeness.length}.
                </p>
              )}
            </div>
          ) : (
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <Link to="/dashboard/foto" className="group rounded-lg border border-border bg-background/40 p-4 transition hover:border-gold/60">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Camera className="h-4 w-4 text-gold" /> Aggiungi nuove foto
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Mostra i tuoi ultimi piatti per restare in evidenza.</p>
              </Link>
              <Link to="/dashboard/profilo" className="group rounded-lg border border-border bg-background/40 p-4 transition hover:border-gold/60">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <UserCog className="h-4 w-4 text-gold" /> Aggiorna il profilo
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Bio, specialità e tariffe sempre allineate al tuo lavoro.</p>
              </Link>
              {tier === "free" ? (
                <Link to="/premium" className="group rounded-lg border border-gold/60 bg-gold-muted/20 p-4 transition hover:border-gold">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Crown className="h-4 w-4 text-gold" /> Passa a Premium
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Più visibilità nei risultati e nella mappa.</p>
                </Link>
              ) : (
                <Link to="/dashboard/annunci" className="group rounded-lg border border-border bg-background/40 p-4 transition hover:border-gold/60">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Megaphone className="h-4 w-4 text-gold" /> Aggiorna l'annuncio
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Mantieni la proposta fresca: cambia titolo, prezzo o foto.</p>
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* Suggerimenti intelligenti — solo se realmente applicabili. */}
      {chef && tips.length > 0 && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl inline-flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-gold" /> Suggerimenti
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Piccole azioni che possono fare una grande differenza.</p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {tips.map((t) => (
              <li key={t.key} className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gold-muted/40 text-gold">
                  <t.icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t.body}</p>
                  <Button asChild size="sm" variant="link" className="mt-1 h-auto px-0 text-xs text-gold">
                    <Link to={t.to}>{t.cta} <ChevronRight className="ml-0.5 h-3 w-3" /></Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Analytics — presentazione migliorata, dati onesti. */}
      {chef && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl inline-flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gold" /> Analytics
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">Ultimi 30 giorni. Aggiornato in tempo reale.</p>
            </div>
          </div>
          <ChefAnalyticsBlock
            views={analyticsQ.data?.profile_views ?? 0}
            impressions={analyticsQ.data?.search_impressions ?? 0}
            waClicks={analyticsQ.data?.whatsapp_clicks ?? 0}
            requests={Math.max(requestsCount, analyticsQ.data?.contact_requests ?? 0)}
            photos={ob?.photos ?? 0}
            loading={analyticsQ.isLoading}
          />
        </div>
      )}

      {/* Timeline — solo eventi realmente accaduti. */}
      {chef && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl inline-flex items-center gap-2">
            <Rocket className="h-5 w-5 text-gold" /> Il tuo percorso
          </h2>
          <ol className="mt-5 space-y-3">
            {timeline.map((step) => (
              <li key={step.key} className="flex items-center gap-3">
                {step.done ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-gold" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-muted-foreground/40" />
                )}
                <span className={`text-sm ${step.done ? "text-foreground" : "text-muted-foreground/70"}`}>{step.label}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Cosa vuoi fare oggi? — card di azione rapida. */}
      <div className="mt-10">
        <h2 className="font-display text-2xl">Cosa vuoi fare oggi?</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {actions.map((a) => (
            <Link
              key={a.label}
              to={a.to}
              className="group rounded-xl border border-border bg-card p-5 transition hover:border-gold hover:shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-gold-muted/40 p-2 text-gold">
                  <a.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{a.label}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{a.desc}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-gold" />
              </div>
            </Link>
          ))}
          {adminQ.data?.isAdmin && (
            <Link to="/dashboard/admin" className="group rounded-xl border border-gold bg-gold/10 p-5 transition hover:bg-gold/20">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-gold/20 p-2 text-gold"><Shield className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">Pannello Admin</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Gestione utenti, verifiche, contenuti</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gold" />
              </div>
            </Link>
          )}
        </div>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {/* Premium / abbonamento — card informativa. */}
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl inline-flex items-center gap-2">
              <Crown className="h-5 w-5 text-gold" /> Premium
            </h2>
            {tier === "free"
              ? <span className="text-xs uppercase tracking-wider text-muted-foreground">Piano Gratuito</span>
              : <TierBadge tier={tier} />}
          </div>
          {isPremium && premiumStatus.premiumUntil && (
            <p className="mt-2 text-sm text-muted-foreground">
              Attivo fino al <strong className="text-foreground">{premiumStatus.premiumUntil.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong> · {remaining} giorni rimanenti
            </p>
          )}
          {!isPremium && (
            <p className="mt-2 text-sm text-muted-foreground">
              Sblocca più foto, video e menù PDF, badge Premium e priorità nei risultati di ricerca.
            </p>
          )}
          <dl className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Piano attivo</dt>
              <dd className="mt-1 font-medium">{PLAN_LABELS[sub?.plan as keyof typeof PLAN_LABELS] ?? "Nessuno"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Origine</dt>
              <dd className="mt-1 font-medium capitalize">{sub?.source === "launch" ? "🎁 Offerta di lancio" : sub?.source ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Data attivazione</dt>
              <dd className="mt-1 font-medium">{sub?.started_at ? new Date(sub.started_at).toLocaleDateString("it-IT") : "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Scadenza</dt>
              <dd className="mt-1 font-medium">{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString("it-IT") : "Nessuna"}</dd>
            </div>
          </dl>
          {isPremium && (
            <ul className="mt-5 grid gap-1.5 text-sm sm:grid-cols-2">
              <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {tier === "gold" ? "Fino a 10 foto pubblicate" : "Fino a 5 foto pubblicate"}</li>
              <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> Video e menù PDF</li>
              <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {tier === "gold" ? "Badge Premium Gold" : "Badge Premium Silver"}</li>
              <li className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {tier === "gold" ? "Massima priorità nei risultati" : "Maggiore visibilità nei risultati"}</li>
            </ul>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            {!isPremium && (
              <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90"><Link to="/premium">Passa a Premium</Link></Button>
            )}
            <Button asChild variant="outline"><Link to="/premium">{isPremium ? "Gestisci Premium" : "Confronta i piani"}</Link></Button>
            {sub?.source === "stripe" && (
              <Button variant="outline" onClick={openPortal}>Portale fatturazione</Button>
            )}
          </div>
        </div>

        {/* Achievements — badge ottenuti / da ottenere. */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-2xl inline-flex items-center gap-2">
            <Trophy className="h-5 w-5 text-gold" /> I tuoi traguardi
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <AchievementTile achieved={!!chef?.founder_badge} label="Founder" sub="Primi 100 chef">
              <FounderBadge />
            </AchievementTile>
            <AchievementTile achieved={!!chef?.verified_badge} label="Verificato" sub="Documenti approvati">
              <VerifiedBadge />
            </AchievementTile>
            <AchievementTile achieved={tier === "silver" || tier === "gold"} label="Premium Silver" sub="Visibilità aumentata">
              <SilverBadge />
            </AchievementTile>
            <AchievementTile achieved={tier === "gold"} label="Premium Gold" sub="Massima visibilità">
              <GoldBadge />
            </AchievementTile>
          </div>
        </div>
      </div>

      {/* Payments */}
      <div className="mt-10 rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl">Storico pagamenti</h2>
        {paymentsQ.data && paymentsQ.data.length > 0 ? (
          <table className="mt-4 w-full text-left text-sm">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr><th className="py-2">Data</th><th>Descrizione</th><th>Importo</th><th>Stato</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paymentsQ.data.map((p) => (
                <tr key={p.id}>
                  <td className="py-2">{new Date(p.created_at).toLocaleDateString("it-IT")}</td>
                  <td>{p.description ?? "—"}</td>
                  <td>{p.amount} {p.currency}</td>
                  <td className="capitalize">{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            {isPremium && sub?.source === "launch"
              ? "Nessun pagamento: stai usando Premium gratuitamente grazie all'offerta di lancio. 🎁"
              : "Non hai ancora effettuato pagamenti. Quando attiverai Premium li vedrai elencati qui."}
          </p>
        )}
      </div>
    </div>
  );
}

function ReactivationCard({
  verified,
  hasActiveListing,
  onReactivated,
}: {
  chefId: string;
  verified: boolean;
  hasActiveListing: boolean;
  onReactivated: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const canReactivate = verified && hasActiveListing;
  const handleClick = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("reactivate_my_profile");
      if (error) throw error;
      const res = data as { ok?: boolean; reason?: string } | null;
      if (!res?.ok) {
        const reason = res?.reason;
        if (reason === "not_verified") toast.error("Il tuo profilo deve essere verificato prima di tornare online.");
        else if (reason === "no_active_listing") toast.error("Devi avere almeno un annuncio attivo per tornare online.");
        else toast.error("Impossibile riattivare il profilo.");
        return;
      }
      toast.success("Profilo di nuovo online.");
      onReactivated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore durante la riattivazione.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6">
      <h3 className="font-display text-2xl">Bentornato! 👋</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Il tuo profilo è stato temporaneamente nascosto perché risultava inattivo.
      </p>
      <p className="mt-3 text-sm font-medium">Prima di tornare online verifica che:</p>
      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
        <li>• il profilo sia aggiornato;</li>
        <li>• le informazioni siano corrette;</li>
        <li>• gli annunci siano ancora validi.</li>
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={handleClick} disabled={loading || !canReactivate}>
          {loading ? "Riattivazione in corso…" : "Rimetti online il mio profilo"}
        </Button>
        {!verified && (
          <span className="text-xs text-muted-foreground">Richiesta verifica approvata.</span>
        )}
        {verified && !hasActiveListing && (
          <span className="text-xs text-muted-foreground">Pubblica almeno un annuncio attivo.</span>
        )}
      </div>
    </div>
  );
}

function AnalyticsStat({
  icon: Icon, label, value, hint, muted,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  muted?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className={`mt-2 font-display text-2xl ${muted ? "text-muted-foreground" : ""}`}>{value}</div>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ChefAnalyticsBlock({
  views, impressions, waClicks, requests, photos, loading,
}: {
  views: number; impressions: number; waClicks: number;
  requests: number; photos: number; loading: boolean;
}) {
  const convPct = views > 0 ? Math.round((requests / views) * 100) : null;
  const v = (n: number) => (loading ? "…" : String(n));
  return (
    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <AnalyticsStat icon={Eye} label="👀 Visualizzazioni profilo"
        value={v(views)} hint="Quante persone hanno aperto la tua pagina." />
      <AnalyticsStat icon={BarChart3} label="🔍 Apparizioni in ricerca"
        value={v(impressions)} hint="Quante volte sei comparso nei risultati." />
      <AnalyticsStat icon={MessageCircle} label="💬 Click WhatsApp"
        value={v(waClicks)} hint="Tap sul pulsante di contatto rapido." />
      <AnalyticsStat icon={Inbox} label="📩 Richieste ricevute"
        value={v(requests)} hint="Form di contatto compilati dai clienti." />
      <AnalyticsStat icon={Camera} label="📸 Foto pubblicate"
        value={v(photos)} hint="Più foto = più fiducia e più richieste." />
      <AnalyticsStat icon={Sparkles}
        label="⭐ Conversione visita → richiesta"
        value={convPct === null ? "—" : `${convPct}%`}
        hint={convPct === null ? "Servono prime visite per calcolarla." : "Percentuale di visitatori che ti contattano."}
        muted={convPct === null}
      />
    </div>
  );
}

function AchievementTile({
  achieved, label, sub, children,
}: {
  achieved: boolean;
  label: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex flex-col items-start gap-2 rounded-xl border p-4 transition ${
        achieved
          ? "border-gold/40 bg-gradient-to-br from-gold-muted/30 to-transparent"
          : "border-dashed border-border bg-background/40 opacity-60"
      }`}
    >
      <div className={achieved ? "" : "grayscale"}>{children}</div>
      <div>
        <p className={`text-sm font-medium ${achieved ? "" : "text-muted-foreground"}`}>{label}</p>
        <p className="text-[11px] text-muted-foreground">{achieved ? "Ottenuto" : sub}</p>
      </div>
    </div>
  );
}